import React, { useState } from 'react';
import { FiX, FiCheck, FiSliders } from 'react-icons/fi';
import type { ServiceApplicationStatus } from '../../../../api/services';

interface UpdateInquiryStatusModalProps {
  guestName: string;
  currentStatus: string;
  onConfirm: (newStatus: ServiceApplicationStatus) => void;
  onCancel: () => void;
}

const statusOptions: {
  id: ServiceApplicationStatus;
  label: string;
  description: string;
  color: string;
}[] = [
  {
    id: 'new',
    label: 'New Inquiry',
    description: 'Freshly received inquiry awaiting response',
    color: 'border-blue-500/40 bg-blue-500/10 text-blue-400',
  },
  {
    id: 'in_conversation',
    label: 'Replied',
    description: 'Communication is ongoing with the guest',
    color: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400',
  },
  {
    id: 'contracted',
    label: 'Offer Sent',
    description: 'Offer agreed upon or contract formed',
    color: 'border-amber-500/40 bg-amber-500/10 text-amber-400',
  },
  {
    id: 'closed',
    label: 'Closed',
    description: 'Inquiry fulfilled, declined, or archived',
    color: 'border-zinc-500/40 bg-zinc-500/10 text-zinc-400',
  },
];

export const UpdateInquiryStatusModal: React.FC<UpdateInquiryStatusModalProps> = ({
  guestName,
  currentStatus,
  onConfirm,
  onCancel,
}) => {
  const mapInitialStatus = (st: string): ServiceApplicationStatus => {
    if (st === 'replied' || st === 'in_conversation') return 'in_conversation';
    if (st === 'offer_sent' || st === 'contracted') return 'contracted';
    if (st === 'closed') return 'closed';
    return 'new';
  };

  const [selectedStatus, setSelectedStatus] = useState<ServiceApplicationStatus>(
    mapInitialStatus(currentStatus),
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm">
      <div
        className="w-full max-w-[460px] bg-[#1C1C1E] border border-[#2C2C2E] rounded-[28px] overflow-hidden shadow-2xl"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-[#2C2C2E]">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600/20 text-[#1784FF]">
              <FiSliders size={18} />
            </div>
            <div>
              <h3 className="text-white text-base font-bold">Update Inquiry Status</h3>
              <p className="text-xs text-zinc-400">{guestName}</p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="p-1.5 text-zinc-400 hover:text-white transition-colors rounded-full hover:bg-white/10"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Options list */}
        <div className="p-6 space-y-2.5 max-h-[60vh] overflow-y-auto no-scrollbar">
          {statusOptions.map((opt) => {
            const isSelected = selectedStatus === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => setSelectedStatus(opt.id)}
                className={`flex w-full items-start justify-between rounded-2xl border p-4 text-left transition-all ${
                  isSelected
                    ? 'border-[#1784FF] bg-[#1B2B4B] shadow-[0_0_15px_rgba(23,132,255,0.2)]'
                    : 'border-[#2C2C2E] bg-[#2A2A2D] hover:bg-[#3A3A3C]'
                }`}
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-white">{opt.label}</span>
                    <span
                      className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${opt.color}`}
                    >
                      {opt.id}
                    </span>
                  </div>
                  <p className="text-xs text-zinc-400">{opt.description}</p>
                </div>
                {isSelected && (
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#1784FF] text-white">
                    <FiCheck size={14} className="stroke-[3]" />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Actions */}
        <div className="flex gap-3 px-6 pb-6 pt-2 border-t border-[#2C2C2E]">
          <button
            onClick={onCancel}
            className="flex-1 h-12 rounded-full border border-white/15 text-white text-sm font-semibold hover:bg-white/5 transition-all cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(selectedStatus)}
            className="flex-1 h-12 rounded-full bg-[#1784FF] text-white text-sm font-semibold hover:bg-blue-600 transition-all shadow-lg flex items-center justify-center gap-2 cursor-pointer"
          >
            <FiCheck size={16} />
            Save Status
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};
